import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { APP_CONFIG, AppConfig } from '@cco-apps/cco-frontend';
import { ResponseDtoService } from '@app/shared/services/response-dto.service';
import { Role, TenantUser, User } from '@cco/model';
import * as Sentry from '@sentry/browser';
import { Observable } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { CognitoTokensWithIotConnectionInfo } from '@core/models/cognito-tokens-with-iot-connection-info';

@Injectable({
  providedIn: 'root',
})
export class Auth2Service extends ResponseDtoService {
  private http = inject(HttpClient);

  readonly relativeUrl: string;

  constructor() {
    const config = inject<AppConfig>(APP_CONFIG);

    super(config.apiBaseUrl);
    this.relativeUrl = `/security`;
  }

  checkAuthenticationStatus(): Observable<User> {
    return this.http.get<User>(`${this.baseUrl + this.relativeUrl}/users/current`).pipe(
      tap((user) => {
        Sentry.setUser({
          username: user.preferredUsername,
          email: user.email,
          id: user.subject,
        });
      }),
    );
  }

  getCurrentTenantUser$(): Observable<TenantUser> {
    return this.tenantBaseUrl$.pipe(switchMap(baseUrl => this.http.get<TenantUser>(
      `${baseUrl}/tenant-users/current`
    )));
  }

  listCurrentUserRoles$(): Observable<Role[]> {
    return this.tenantBaseUrl$.pipe(switchMap(baseUrl => this.http.get<Role[]>(
      `${baseUrl + this.relativeUrl}/users/current/roles`
    )));
  }

  getToken$(aud: string): Observable<string> {
    return this.tenantBaseUrl$.pipe(switchMap(baseUrl => this.http.get(`${baseUrl + this.relativeUrl}/users/current/token`, {
      params: {
        aud,
      },
      responseType: 'text',
    })));
  }

  getIotConnectionInfo$(): Observable<CognitoTokensWithIotConnectionInfo> {
    return this.tenantBaseUrl$.pipe(switchMap(baseUrl =>
      this.http.get<CognitoTokensWithIotConnectionInfo>(`${baseUrl + this.relativeUrl}/users/current/iot-connection-info`)));
  }

  attachIotPolicy(identityId: string): Observable<any> {
    return this.tenantBaseUrl$.pipe(switchMap(baseUrl => this.http.put(`${baseUrl + this.relativeUrl}/users/current/attach-iot-policy`, { identityId })));
  }

  getCognitoFederationToken$(): Observable<string> {
    return this.tenantBaseUrl$.pipe(switchMap(baseUrl => this.http.get(`${baseUrl + this.relativeUrl}/users/current/cognito-federation-token`, {
      responseType: 'text',
    })));
  }
}
